import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLoads } from "@/context/LoadsContext";
import { AppShell } from "@/components/AppShell";
import { generateRateConfirmationPDF } from "@/utils/generatePDF";
import { toast } from "sonner";
import { Truck, DollarSign, MapPinned, ClipboardList, FileDown, Save, ArrowLeft, Calculator, Info } from "lucide-react";

const SectionHeader = ({ step, icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-4 mb-6">
    <div
      className="h-12 w-12 shrink-0 rounded-xl bg-[#F9DB81] flex items-center justify-center border-2 border-black"
      style={{ boxShadow: "3px 3px 0 0 rgba(255,255,255,0.85)" }}
    >
      <Icon size={22} color="#0A0A0A" strokeWidth={2.6} />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-display font-bold text-[#F9DB81] mb-1">
        Step {step}
      </div>
      <div className="font-display font-black text-2xl tracking-tight">{title}</div>
      <div className="text-zinc-400 text-sm mt-1">{subtitle}</div>
    </div>
  </div>
);

const Field = ({ label, children, required, full, testId }) => (
  <div className={full ? "lg:col-span-2" : ""}>
    <label className="rd-label" data-testid={testId ? `${testId}-label` : undefined}>
      {label} {required && <span className="text-[#F9DB81]">*</span>}
    </label>
    {children}
  </div>
);

const initialForm = {
  loadNumber: `RD-${10500 + Math.floor(Math.random() * 500)}`,
  customer: "",
  carrierName: "",
  carrierMC: "",
  driverName: "",
  driverPhone: "",
  customerRate: "",
  carrierPay: "",
  fscRate: "7", // % of carrier pay (auto FSC)
  origin: "",
  destination: "",
  shipperAddress: "",
  consigneeAddress: "",
  pickupDate: "",
  pickupTime: "",
  deliveryDate: "",
  deliveryTime: "",
  notes: "",
  status: "Assigned",
};

export default function NewLoad() {
  const navigate = useNavigate();
  const { addLoad } = useLoads();
  const [form, setForm] = useState(initialForm);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fsc = useMemo(() => {
    const pay = Number(form.carrierPay) || 0;
    const rate = Number(form.fscRate) || 0;
    return +(pay * (rate / 100)).toFixed(2);
  }, [form.carrierPay, form.fscRate]);

  const margin = useMemo(() => {
    const cust = Number(form.customerRate) || 0;
    const pay = Number(form.carrierPay) || 0;
    return +(cust - pay - fsc).toFixed(2);
  }, [form.customerRate, form.carrierPay, fsc]);

  const marginPct = useMemo(() => {
    const cust = Number(form.customerRate) || 0;
    if (!cust) return 0;
    return +((margin / cust) * 100).toFixed(1);
  }, [margin, form.customerRate]);

  const buildPayload = () => ({
    id: `L-${Date.now()}`,
    loadNumber: form.loadNumber,
    customer: form.customer,
    carrierName: form.carrierName,
    carrierMC: form.carrierMC,
    driverName: form.driverName,
    driverPhone: form.driverPhone,
    origin: form.origin,
    destination: form.destination,
    customerRate: Number(form.customerRate) || 0,
    carrierPay: Number(form.carrierPay) || 0,
    fsc,
    status: form.status,
    shipperAddress: form.shipperAddress,
    consigneeAddress: form.consigneeAddress,
    pickupDate: form.pickupDate,
    pickupTime: form.pickupTime,
    deliveryDate: form.deliveryDate,
    deliveryTime: form.deliveryTime,
    notes: form.notes,
  });

  const validate = () => {
    if (!form.loadNumber.trim()) return "Load # is required";
    if (!form.carrierName.trim()) return "Assigned Carrier Name is required";
    if (!form.driverPhone.trim()) return "Driver Phone is required";
    if (!form.origin.trim() || !form.destination.trim()) return "Origin and Destination are required";
    if (!form.customerRate || !form.carrierPay) return "Customer Rate and Carrier Pay are required";
    return null;
  };

  const handleSaveAndBack = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    addLoad(buildPayload());
    toast.success(`Load ${form.loadNumber} saved`, { description: "Added to your active board." });
    navigate("/dashboard");
  };

  const handleGeneratePDF = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    const payload = buildPayload();
    addLoad(payload);
    generateRateConfirmationPDF(payload);
    toast.success("Rate Confirmation generated", { description: `PDF downloaded for ${payload.loadNumber}.` });
  };

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-[1100px] mx-auto pb-28 rd-fade-up">
        {/* Header */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-zinc-400 hover:text-[#F9DB81] mb-6 text-sm font-semibold transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <div className="mb-8">
          <div className="rd-section-tag mb-3">◆ New Booking</div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight">
            Book New Load
          </h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">
            Fill out the load, carrier, and routing details. We'll auto-calculate fuel surcharge and instantly generate a downloadable Carrier Rate Confirmation PDF.
          </p>
        </div>

        {/* STEP 1 — Load & Carrier */}
        <section className="rd-card p-6 lg:p-8 mb-8">
          <SectionHeader step="01" icon={Truck} title="Load & Carrier Info" subtitle="Who's hauling, who's billing." />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Field label="Load #" required testId="loadNumber">
              <input className="rd-input" value={form.loadNumber} onChange={update("loadNumber")} data-testid="loadNumber-input" />
            </Field>
            <Field label="Customer (Bill To)" testId="customer">
              <input className="rd-input" value={form.customer} onChange={update("customer")} placeholder="Walmart DC #6094" data-testid="customer-input" />
            </Field>
            <Field label="Assigned Carrier Name" required testId="carrierName">
              <input className="rd-input" value={form.carrierName} onChange={update("carrierName")} placeholder="Bluebird Trucking LLC" data-testid="carrierName-input" />
            </Field>
            <Field label="Carrier MC #" testId="carrierMC">
              <input className="rd-input" value={form.carrierMC} onChange={update("carrierMC")} placeholder="MC-887421" data-testid="carrierMC-input" />
            </Field>
            <Field label="Driver Name" testId="driverName">
              <input className="rd-input" value={form.driverName} onChange={update("driverName")} placeholder="Marcus Reyes" data-testid="driverName-input" />
            </Field>
            <Field label="Driver Phone" required testId="driverPhone">
              <input className="rd-input" value={form.driverPhone} onChange={update("driverPhone")} placeholder="(404) 555-2381" data-testid="driverPhone-input" />
            </Field>
            <Field label="Status" testId="status">
              <select className="rd-input" value={form.status} onChange={update("status")} data-testid="status-select">
                <option>Assigned</option>
                <option>Dispatched</option>
                <option>In Transit</option>
                <option>Delivered</option>
              </select>
            </Field>
          </div>
        </section>

        {/* STEP 2 — Financials */}
        <section className="rd-card p-6 lg:p-8 mb-8">
          <SectionHeader step="02" icon={DollarSign} title="Financials" subtitle="Set the rates. We'll calc the rest." />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Field label="Customer Rate ($)" required testId="customerRate">
              <input type="number" min="0" step="0.01" className="rd-input" value={form.customerRate} onChange={update("customerRate")} placeholder="3800" data-testid="customerRate-input" />
            </Field>
            <Field label="Carrier Pay ($)" required testId="carrierPay">
              <input type="number" min="0" step="0.01" className="rd-input" value={form.carrierPay} onChange={update("carrierPay")} placeholder="3100" data-testid="carrierPay-input" />
            </Field>
            <Field label="FSC Rate (% of Carrier Pay)" testId="fscRate">
              <input type="number" min="0" step="0.1" className="rd-input" value={form.fscRate} onChange={update("fscRate")} placeholder="7" data-testid="fscRate-input" />
            </Field>
            <Field label="Fuel Surcharge (auto)" testId="fscCalculated">
              <div className="rd-input flex items-center gap-2 bg-black/70" data-testid="fscCalculated-display">
                <Calculator size={15} className="text-[#F9DB81]" />
                <span className="font-display font-bold">
                  ${fsc.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </span>
              </div>
            </Field>
          </div>

          {/* Live margin readout */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rd-card p-4 bg-black/40">
              <div className="rd-label">Total Carrier Cost</div>
              <div className="font-display font-black text-2xl">
                ${(Number(form.carrierPay || 0) + fsc).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div className="rd-card p-4 bg-black/40">
              <div className="rd-label">Broker Margin ($)</div>
              <div
                className="font-display font-black text-2xl"
                style={{ color: margin >= 0 ? "#10B981" : "#EF4444" }}
                data-testid="margin-dollar"
              >
                ${margin.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div className="rd-card p-4 bg-black/40">
              <div className="rd-label">Broker Margin (%)</div>
              <div
                className="font-display font-black text-2xl"
                style={{ color: marginPct >= 0 ? "#F9DB81" : "#EF4444" }}
                data-testid="margin-percent"
              >
                {marginPct.toFixed(1)}%
              </div>
            </div>
          </div>
        </section>

        {/* STEP 3 — Routing */}
        <section className="rd-card p-6 lg:p-8 mb-8">
          <SectionHeader step="03" icon={MapPinned} title="Routing Details" subtitle="Shipper, consignee, dates and appointments." />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Field label="Origin (City, State)" required testId="origin">
              <input className="rd-input" value={form.origin} onChange={update("origin")} placeholder="Atlanta, GA" data-testid="origin-input" />
            </Field>
            <Field label="Destination (City, State)" required testId="destination">
              <input className="rd-input" value={form.destination} onChange={update("destination")} placeholder="Dallas, TX" data-testid="destination-input" />
            </Field>
            <Field label="Shipper Address" full testId="shipperAddress">
              <input className="rd-input" value={form.shipperAddress} onChange={update("shipperAddress")} placeholder="1240 Industrial Blvd, Atlanta, GA 30318" data-testid="shipperAddress-input" />
            </Field>
            <Field label="Consignee Address" full testId="consigneeAddress">
              <input className="rd-input" value={form.consigneeAddress} onChange={update("consigneeAddress")} placeholder="8800 Stemmons Fwy, Dallas, TX 75247" data-testid="consigneeAddress-input" />
            </Field>
            <Field label="Pickup Date" testId="pickupDate">
              <input type="date" className="rd-input" value={form.pickupDate} onChange={update("pickupDate")} data-testid="pickupDate-input" />
            </Field>
            <Field label="Pickup Appt Time" testId="pickupTime">
              <input type="time" className="rd-input" value={form.pickupTime} onChange={update("pickupTime")} data-testid="pickupTime-input" />
            </Field>
            <Field label="Delivery Date" testId="deliveryDate">
              <input type="date" className="rd-input" value={form.deliveryDate} onChange={update("deliveryDate")} data-testid="deliveryDate-input" />
            </Field>
            <Field label="Delivery Appt Time" testId="deliveryTime">
              <input type="time" className="rd-input" value={form.deliveryTime} onChange={update("deliveryTime")} data-testid="deliveryTime-input" />
            </Field>
          </div>
        </section>

        {/* STEP 4 — Notes */}
        <section className="rd-card p-6 lg:p-8 mb-8">
          <SectionHeader step="04" icon={ClipboardList} title="Special Instructions" subtitle="Gate instructions, reload codes, lumper info — anything the driver needs." />
          <Field label="Broker Notes / Gate Instructions for Driver" testId="notes">
            <textarea
              rows={6}
              className="rd-input resize-y"
              value={form.notes}
              onChange={update("notes")}
              placeholder="e.g. Tell the gate guard 'Pickup for AMZL-44'. Reload code HD-2240. Scale ticket required. No early arrivals."
              data-testid="notes-input"
            />
          </Field>
          <div className="flex items-start gap-2 mt-4 text-xs text-zinc-500">
            <Info size={14} className="shrink-0 mt-0.5 text-[#F9DB81]" />
            <span>These notes will appear directly on the carrier's Rate Confirmation PDF and are legally binding once acknowledged.</span>
          </div>
        </section>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-md border-t-2 border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm">
            <div className="rd-sticker bg-[#F9DB81] text-black border-black" style={{ boxShadow: "3px 3px 0 0 #0A0A0A" }}>
              Margin: ${margin.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </div>
            <span className="text-zinc-500 hidden sm:inline">Ref #: <span className="text-white font-mono">{form.loadNumber}</span></span>
          </div>
          <div className="flex gap-3">
            <button onClick={handleSaveAndBack} className="rd-btn-secondary px-5 py-3 text-sm flex items-center gap-2" data-testid="save-load-button">
              <Save size={15} strokeWidth={3} /> Save Load
            </button>
            <button onClick={handleGeneratePDF} className="rd-btn-primary px-5 py-3 text-sm flex items-center gap-2" data-testid="generate-pdf-button">
              <FileDown size={15} strokeWidth={3} /> Generate Rate Confirmation (PDF)
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
